import { RestResource } from "../common"
import { SchemaDeSerBuilder, type SchemaDeSer } from "../deser"
import {
  ACH_RELATIONSHIP_NEED_REVIEW_STATUSES,
  ACH_RELATIONSHIP_STATUSES,
  BANK_ACCOUNT_TYPES,
  INSTITUTION_STATUSES,
} from "./shared"

export class ExternalInstitution extends RestResource {
  accountNumber = ""
  institutionType = ""
  achRelationshipId = 0
  status: INSTITUTION_STATUSES | null = null
  loginExpiredAt: Date | null = null

  get isReauthRequired(): boolean {
    return this.status === INSTITUTION_STATUSES.REAUTH_REQUIRED
  }

  get isLoginExpired(): boolean {
    return this.loginExpiredAt !== null
  }
}

export const EXTERNAL_INSTITUTION_DESER: SchemaDeSer<ExternalInstitution> =
  new SchemaDeSerBuilder(ExternalInstitution)
    .ofString("id")
    .ofStrings("accountNumber", "institutionType", "status")
    .ofInt("achRelationshipId")
    .ofDateTime("loginExpiredAt")
    .toDeSer()

export class AchRelationship extends RestResource {
  accountNumber = ""
  approvalMethod = ""
  achRelationshipMethod = ""
  extRelationshipId = ""
  achRelationshipStatus = ""
  cancellationComment = ""
  externalAccountId = ""
  externalAccountType = ""
  externalAccountNumber = ""
  institutionType = ""
  nickname = ""
  publicToken = ""
  bankRoutingNumber = ""
  bankAccount = ""
  bankAccountNumberMask = ""
  bankAccountOwnerName = ""
  approvalAttemptsCount = 0
  maxApprovalAttempts = false
  bankAccountType = BANK_ACCOUNT_TYPES.CHECKING
  externalInstitution: ExternalInstitution | null = null
  hasUsedRtpDeposit = false
  isRtpDepositEligible = false

  get isApproved() {
    return this.achRelationshipStatus === ACH_RELATIONSHIP_STATUSES.APPROVED
  }

  get isPresend() {
    return this.achRelationshipStatus === ACH_RELATIONSHIP_STATUSES.PRE_SEND
  }

  get isPending() {
    return this.achRelationshipStatus === ACH_RELATIONSHIP_STATUSES.PENDING
  }

  get isIdentityFail() {
    return (
      this.achRelationshipStatus === ACH_RELATIONSHIP_STATUSES.IDENTITY_FAIL
    )
  }

  get isIdentityMissing() {
    return (
      this.achRelationshipStatus === ACH_RELATIONSHIP_STATUSES.IDENTITY_MISSING
    )
  }

  get isRejected() {
    return this.achRelationshipStatus === ACH_RELATIONSHIP_STATUSES.REJECTED
  }

  get isCanceled() {
    return this.achRelationshipStatus === ACH_RELATIONSHIP_STATUSES.CANCELED
  }

  get isPreviousAchReturned() {
    return (
      this.achRelationshipStatus ===
      ACH_RELATIONSHIP_STATUSES.PREVIOUS_ACH_RETURNED_R10
    )
  }

  get hasCompletedRelationship() {
    return this.isCreated && !this.isPresend
  }

  get isTerminal() {
    return this.isRejected || this.isCanceled
  }

  get isNeedReviewStatus() {
    return ACH_RELATIONSHIP_NEED_REVIEW_STATUSES.includes(
      this.achRelationshipStatus as ACH_RELATIONSHIP_STATUSES
    )
  }

  get isIdentityFailureStatus() {
    return this.isIdentityFail || this.isIdentityMissing
  }
}

export const NO_ACH_RELATIONSHIP = new AchRelationship()

export const ACH_RELATIONSHIP_DESER: SchemaDeSer<AchRelationship> =
  new SchemaDeSerBuilder(AchRelationship)
    .ofString("accountNumber")
    .ofString("achRelationshipStatus")
    .ofString("approvalMethod")
    .ofString("achRelationshipMethod")
    .ofString("cancellationComment")
    .ofString("externalAccountId")
    .ofString("externalAccountType")
    .ofString("externalAccountNumber")
    .ofString("id")
    .ofString("institutionType")
    .ofString("nickname")
    .ofString("publicToken")
    .ofString("bankRoutingNumber")
    .ofString("bankAccount")
    .ofString("bankAccountNumberMask")
    .ofString("bankAccountOwnerName")
    .ofString("bankAccountType")
    .ofString("extRelationshipId")
    .ofBoolean("maxApprovalAttempts")
    .ofInt("approvalAttemptsCount")
    .ofNested(
      "externalInstitution",
      EXTERNAL_INSTITUTION_DESER,
      ExternalInstitution
    )
    .ofBoolean("hasUsedRtpDeposit")
    .ofBoolean("isRtpDepositEligible")
    .toDeSer()
